import type React from 'react';
import { useMemo, useState } from 'react';
import Tooltip from '@components/Tooltip';
import { Button } from '@components/ui/button';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { datapointFieldSelector } from '@ContractBuilder/utils/datapoint-field-selector';
import type { FormError, JSONSchemaProperties } from '@root/helpers/schema/types';
import { ChevronsDown, ChevronsUp, Plus } from 'lucide-react';

import type { ScheduleItemRowProps } from './ScheduleItemRow';
import { ScheduleItemRow } from './ScheduleItemRow';
import {
  addChildAtPath,
  addTopLevelItem,
  collectAllPathKeys,
  countChildrenAtPath,
  countSiblingsAtPath,
  moveItemAtPath,
  pathKey,
  remapKeysForMove,
  remapKeysForRemove,
  removeItemAtPath,
  schedulePathToOwnKey,
  updateItemAtPath,
} from './tree-operations';
import type { ScheduleItemValue, SchedulePath } from './types';

interface ScheduleTreeProps {
  item: JSONSchemaProperties & { 'ui:itemLabel'?: string };
  ownKey: string;
  onChange?: (value: ScheduleItemValue[], name?: string) => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  /** Passed down by mapChildren; every field in the tree resolves its own errors out of this. */
  validationErrors?: FormError[] | null;
  /**
   * Forwarded to every panel field. ContractForm sets it for the whole form, so a field marks
   * itself incomplete (the amber border) without also printing the message underneath — the
   * schedule has to pass it on or its rows would be the only fields in the form that do.
   */
  hideErrors?: boolean;
  /** Handed down by mapChildren; rows use it to render their fields. */
  MakeComponent: ScheduleItemRowProps['MakeComponent'];
}

const ScheduleTree: React.FC<ScheduleTreeProps> = ({
  item,
  ownKey,
  onChange,
  isReadOnly = false,
  isDisabled = false,
  validationErrors,
  hideErrors,
  MakeComponent,
}) => {
  const selector = useMemo(() => datapointFieldSelector({ ownKey }), [ownKey]);
  const { value } = useDatapointsStore(selector);
  const tree = (value as ScheduleItemValue[] | undefined) ?? [];

  const itemLabel = item['ui:itemLabel'] ?? 'Item';
  const addLabel = `Add a ${itemLabel}`;

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const emitChange = (nextTree: ScheduleItemValue[]) => onChange?.(nextTree, ownKey);

  const isExpanded = (path: SchedulePath) => expandedKeys.has(pathKey(path));

  const handleToggleExpand = (path: SchedulePath) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      const key = pathKey(path);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleExpandAll = () => setExpandedKeys(new Set(collectAllPathKeys(tree)));
  const handleCollapseAll = () => setExpandedKeys(new Set());

  const handleAddTopLevel = () => emitChange(addTopLevelItem(tree));

  const handleAddChild = (path: SchedulePath) => emitChange(addChildAtPath(tree, path));

  const handleUpdate = (path: SchedulePath, patch: Partial<ScheduleItemValue>) =>
    emitChange(updateItemAtPath(tree, path, patch));

  // Expanded state is positional, so it has to be renumbered alongside the tree — otherwise the
  // row that takes a moved item's index inherits its expanded state.
  const handleRemove = (path: SchedulePath) => {
    const promotedCount = countChildrenAtPath(tree, path);
    setExpandedKeys((prev) => remapKeysForRemove(prev, path, promotedCount));
    emitChange(removeItemAtPath(tree, path));
  };

  const handleMove = (path: SchedulePath, direction: -1 | 1) => {
    const target = path[path.length - 1] + direction;
    const siblingsCount = countSiblingsAtPath(tree, path);
    if (target < 0 || target >= siblingsCount) return;

    setExpandedKeys((prev) => remapKeysForMove(prev, path, direction));
    emitChange(moveItemAtPath(tree, path, direction));
  };

  const handleMoveUp = (path: SchedulePath) => handleMove(path, -1);
  const handleMoveDown = (path: SchedulePath) => handleMove(path, 1);

  if (tree.length === 0) {
    // A read-only surface shows nothing at all for an empty schedule — the "Add a …" call to
    // action is the only thing here, and offering it would imply an edit that cannot happen.
    if (isReadOnly) return null;

    return (
      <div data-testid="schedule-tree-empty">
        <Button type="button" onClick={handleAddTopLevel} disabled={isDisabled}>
          <Plus className="size-4" />
          {addLabel}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="schedule-tree">
      <div className="flex justify-end gap-2">
        <Tooltip content="Expand all">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Expand all"
            data-testid="schedule-expand-all"
            onClick={handleExpandAll}
          >
            <ChevronsDown className="size-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Collapse all">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Collapse all"
            data-testid="schedule-collapse-all"
            onClick={handleCollapseAll}
          >
            <ChevronsUp className="size-4" />
          </Button>
        </Tooltip>
      </div>

      {tree.map((scheduleItem, index) => (
        <ScheduleItemRow
          key={index}
          item={scheduleItem}
          path={[index]}
          siblingsCount={tree.length}
          itemLabel={itemLabel}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
          onUpdate={handleUpdate}
          onAddChild={handleAddChild}
          onRemove={handleRemove}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          itemSchema={item.items as never}
          rowOwnKey={schedulePathToOwnKey(ownKey, [index])}
          onFieldChange={onChange}
          MakeComponent={MakeComponent}
          validationErrors={validationErrors}
          hideErrors={hideErrors}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
        />
      ))}

      {!isReadOnly && (
        <Button type="button" variant="outline" className="mt-3" onClick={handleAddTopLevel} disabled={isDisabled}>
          <Plus className="size-4" />
          {addLabel}
        </Button>
      )}
    </div>
  );
};

export default ScheduleTree;
